import axios from 'axios';
import {
  type PasswordChangeStatus,
  type TokenPair,
  clearTokens,
  getAccessToken,
  getRefreshToken,
} from './auth-storage';

// API URL 설정
const API_URL = import.meta.env.PUBLIC_API_URL;

/**
 * API 응답 형식
 */
interface ApiResponse<T> {
  message: string;
  data: T;
}

/**
 * 로그인 응답 데이터 타입
 */
export interface LoginResponseData {
  message: string;
  user: {
    id: string;
    email: string;
    name: string;
    lang: string;
    roles: string[];
    companyId: string;
    isRecordingConsented: boolean | null;
    passwordChange?: PasswordChangeStatus;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: string | number;
  };
}

/**
 * 로그인 응답 타입
 */
export type LoginResponse = LoginResponseData;

/**
 * 로그인 요청 인터페이스
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * 로그인 API 호출
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const loginUrl = `${API_URL}/v2/auth/login`;

  try {
    const response = await axios.post(loginUrl, credentials);

    // 서버 응답 형식에 따라 적절히 처리
    // API 응답이 { message, data: { message, user, tokens } } 형식이라면
    const apiResponse = response.data as ApiResponse<LoginResponseData>;
    const responseData = apiResponse.data || response.data;

    // 응답 구조 검증
    if (!responseData.user || !responseData.tokens) {
      throw new Error('로그인 응답 구조가 올바르지 않습니다');
    }

    return responseData;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 로그아웃
 */
export function logout(): void {
  clearTokens();
  // 로그인 페이지로 리다이렉트하거나 필요한 추가 로직
}

/**
 * 리프레시 토큰을 사용하여 액세스 토큰 갱신
 */
export async function refreshTokenApi(): Promise<TokenPair> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('Refresh token not found');
  }

  try {
    const response = await axios.post(`${API_URL}/v2/auth/refresh-token`, {
      refreshToken,
    });

    // 서버 응답 형식에 따라 적절히 처리
    const tokens = response.data.data || response.data;

    if (!tokens.accessToken || !tokens.refreshToken) {
      throw new Error('토큰 응답 구조가 올바르지 않습니다');
    }

    return tokens;
  } catch (error) {
    clearTokens(); // 리프레시 실패시 로그아웃
    throw handleAuthError(error);
  }
}

/**
 * 비밀번호 재설정
 */
export async function resetPasswordApi(
  newPassword: string,
  accessToken?: string,
): Promise<{ user: LoginResponseData['user']; tokens: TokenPair } | null> {
  const token = accessToken || getAccessToken();
  if (!token) {
    throw new Error('Authentication token is required');
  }

  try {
    // 변경 주기가 지나 차단된 상태에서도 호출할 수 있는 엔드포인트다.
    // 기존 토큰에는 변경 전 기준 시각이 박혀 있어 새 토큰을 함께 받아 갈아끼운다.
    const response = await axios.post(
      `${API_URL}/v2/auth/reset-password`,
      { newPassword },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data?.data ?? null;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 현재 사용자 정보 가져오기
 */
export async function getCurrentUser() {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await axios.get(`${API_URL}/v2/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data?.data || response.data;
    return data.user ?? data;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 특정 액세스 토큰으로 사용자 정보 가져오기 (magic link용)
 */
export async function fetchMeWithToken(accessToken: string) {
  try {
    const response = await axios.get(`${API_URL}/v2/auth/me`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const data = response.data?.data || response.data;
    return data.user ?? data;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 서버가 내려준 errorCode 를 들고 다니는 에러.
 *
 * 계정 잠금·인증번호 흐름은 화면이 사유별로 다르게 분기해야 하는데, 메시지 문자열만
 * 남기면 그 분기를 할 수 없다. Error 를 상속하므로 기존 호출부는 그대로 동작한다.
 */
export class AuthApiError extends Error {
  readonly errorCode?: string;
  /** HTTP 상태코드. 429(요청 과다)·5xx 를 비밀번호 오류와 구분하는 데 쓴다 */
  readonly status?: number;
  readonly lockExpiresAt?: string | null;
  readonly remainingLockMinutes?: number;

  constructor(
    message: string,
    detail: {
      errorCode?: string;
      status?: number;
      lockExpiresAt?: string | null;
      remainingLockMinutes?: number;
    } = {},
  ) {
    super(message);
    this.name = 'AuthApiError';
    this.errorCode = detail.errorCode;
    this.status = detail.status;
    this.lockExpiresAt = detail.lockExpiresAt;
    this.remainingLockMinutes = detail.remainingLockMinutes;
  }
}

/**
 * 인증 에러 처리
 */
function handleAuthError(error: any): Error {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const message = data?.message || error.message;
    return new AuthApiError(
      Array.isArray(message) ? message.join('\n') : message,
      {
        errorCode: data?.errorCode,
        status: error.response?.status,
        lockExpiresAt: data?.lockExpiresAt,
        remainingLockMinutes: data?.remainingLockMinutes,
      },
    );
  }
  return error instanceof Error
    ? error
    : new Error('인증 중 알 수 없는 오류가 발생했습니다');
}

/**
 * 계정 잠금 해제 인증번호 재발송.
 * 계정이 없거나 잠기지 않았어도 서버는 성공으로 응답한다(가입 여부 노출 방지).
 */
export async function requestAccountUnlockCode(email: string): Promise<void> {
  try {
    await axios.post(`${API_URL}/v2/auth/unlock/code`, { email });
  } catch (error) {
    throw handleAuthError(error);
  }
}

/** 잠금 해제 인증번호 확인 */
export async function verifyAccountUnlockCode(
  email: string,
  code: string,
): Promise<void> {
  try {
    await axios.post(`${API_URL}/v2/auth/unlock/verify-code`, { email, code });
  } catch (error) {
    throw handleAuthError(error);
  }
}

/** 인증번호로 잠금 해제 + 비밀번호 재설정 */
export async function unlockAccount(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  try {
    await axios.post(`${API_URL}/v2/auth/unlock/password`, {
      email,
      code,
      newPassword,
    });
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 초기 비밀번호 설정 코드 검증
 */
export async function verifyInitialPasswordSetupCode(code: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v2/auth/verify-initial-password-setup-code?code=${code}`,
    );
    return response.data;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 초기 비밀번호 설정
 */
export async function setupInitialPassword(code: string, password: string) {
  try {
    const response = await axios.post(
      `${API_URL}/v2/auth/setup-initial-password`,
      { code, password },
    );
    return response.data;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * HR 비밀번호 설정 + 자동 로그인.
 * 일반 setupInitialPassword와 달리 토큰을 함께 받아 재로그인 없이 대시보드로 진입한다.
 */
export async function setupInitialPasswordForHr(
  code: string,
  password: string,
): Promise<LoginResponse> {
  try {
    const response = await axios.post(
      `${API_URL}/v2/auth/hr/initial-password`,
      { code, password },
    );
    const apiResponse = response.data as ApiResponse<LoginResponseData>;
    const responseData = apiResponse.data || response.data;

    if (!responseData.user || !responseData.tokens) {
      throw new Error('로그인 응답 구조가 올바르지 않습니다');
    }
    return responseData;
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 비밀번호 재설정 링크 검증
 */
export async function verifyPasswordResetCode(code: string) {
  try {
    const response = await axios.post(
      `${API_URL}/v2/auth/verify-password-reset-code`,
      { code },
    );
    return response.data;
  } catch (error) {
    console.error('verifyPasswordResetCode error:', error);
    throw handleAuthError(error);
  }
}

/**
 * 전사 및 AI 요약 동의 처리
 */
export async function consentRecordingApi(consent: boolean): Promise<void> {
  const token = getAccessToken();
  if (!token) throw new Error('Authentication token not found');

  try {
    await axios.patch(
      `${API_URL}/v2/auth/consent/recording`,
      { consent },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (error) {
    throw handleAuthError(error);
  }
}

/**
 * 비밀번호 재설정
 */
export async function resetPasswordFromLink(code: string, newPassword: string) {
  try {
    const response = await axios.post(
      `${API_URL}/v2/auth/reset-password-from-link`,
      { code, newPassword },
    );
    return response.data;
  } catch (error) {
    throw handleAuthError(error);
  }
}
